import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import Swal from 'sweetalert2';

import { BiensImmobs } from 'app/models/biensImmobs';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { BiensImmobsBiensImmobs } from 'app/services/biens-immobs/biens-immobs.service';
import { AwscontrolService, UploadResponse, DeleteResponse } from 'app/services/aws/awscontrol.service';

@Component({
  selector: 'app-modifier-biens-immob',
  templateUrl: './modifier-biens-immob.component.html',
  styleUrls: ['./modifier-biens-immob.component.scss']
})
export class ModifierBiensImmobComponent implements OnInit {
  @ViewChild('descriptionTextarea', { static: false }) descriptionTextarea!: ElementRef;

  // Form data
  biens: BiensImmobs;
  
  // File upload – single main image
  selectedFile: File | null = null;
  mainImage: string = '';       // URL of the main image
  
  // Agencies
  selectedAgencyName: string = '';
  
  // Agency search functionality
  public agencySearch: string = '';
  public filteredAgencies: any[] = [];
  public allAgencies: any[] = [];
  public isAgencySelectOpen: boolean = false;
  
  // Categories
  public categoryInput: string = '';
  
  // Other
  public biensImmob: any;
  public idBienImmobs: any;
  public agencyId: string | null = null; // <-- store agency ID for navigation
  dataSource = new MatTableDataSource();

  // Upload states
  public isUploading: boolean = false;
  public uploadProgress: number = 0;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private _location: Location,
    private agenciesService: AgenciesService,
    private bienImmobService: BiensImmobsBiensImmobs,
    private awsService: AwscontrolService
  ) {
    this.biens = new BiensImmobs();
  }

  ngOnInit(): void {
    this.idBienImmobs = this.activatedRoute.snapshot.paramMap.get('id');
    this.getData();
    this.loadAgencies();
  }

  getData() {
    // Use 'any' to access dynamic properties like user, userId, agencyId
    this.bienImmobService.getBiensImmobs(this.idBienImmobs).subscribe((data: any) => {
      console.log('Bien data loaded:', data);
      this.biens = data;
      
      // Initialize main image
      if (data.imageUrl) {
        this.mainImage = data.imageUrl;
      }
      
      // Initialize agency name
      if (data.agencyName) {
        if (typeof data.agencyName === 'object') {
          const agencyObj = data.agencyName as any;
          this.selectedAgencyName = agencyObj.name || '';
        } else {
          this.selectedAgencyName = data.agencyName;
        }
      }
      
      // Extract agency ID for navigation
      if (data.user?.id) {
        this.agencyId = data.user.id.toString();
      } else if (data.userId) {
        this.agencyId = data.userId.toString();
      } else if (data.agencyId) {
        this.agencyId = data.agencyId.toString();
      }
      
      // Initialize categories
      if (!this.biens.categoryList) {
        this.biens.categoryList = [];
      }
      
      console.log('Property loaded successfully:', this.biens);
      console.log('Agency ID for navigation:', this.agencyId);
    });
  }

  loadAgencies() {
    this.agenciesService.getList().subscribe({
      next: (data: any) => {
        if (Array.isArray(data)) {
          this.allAgencies = data
            .filter(agency => agency && agency.name && typeof agency.name === 'string' && (agency.role =='meilleurs-biens' || agency.role =='agence-immobilieres'))
            .map(agency => ({
              id: agency.id,
              name: agency.name.trim()
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
        } else if (data && typeof data === 'object' && data.name) {
          this.allAgencies = [{
            id: data.id,
            name: data.name.trim()
          }];
        } else {
          this.allAgencies = [];
        }
        
        this.filteredAgencies = [...this.allAgencies];
      },
      error: (error) => {
        console.error('Error loading agencies:', error);
        this.allAgencies = [];
        this.filteredAgencies = [];
      }
    });
  }

  // Text formatting
  formatText(command: string): void {
    if (this.descriptionTextarea) {
      const textarea = this.descriptionTextarea.nativeElement;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = this.biens.description.substring(start, end);
      
      let formattedText = '';
      switch (command) {
        case 'bold':
          formattedText = `<strong>${selectedText}</strong>`;
          break;
        case 'italic':
          formattedText = `<em>${selectedText}</em>`;
          break;
        case 'underline':
          formattedText = `<u>${selectedText}</u>`;
          break;
      }
      
      const newText = this.biens.description.substring(0, start) + 
                     formattedText + 
                     this.biens.description.substring(end);
      
      this.biens.description = newText;
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
      }, 0);
    }
  }

  // Categories management
  addCategory(): void {
    if (this.categoryInput && this.categoryInput.trim() !== '') {
      if (!this.biens.categoryList) {
        this.biens.categoryList = [];
      }
      this.biens.categoryList.push(this.categoryInput.trim());
      this.categoryInput = '';
    }
  }

  removeCategory(category: string): void {
    if (this.biens.categoryList) {
      const index = this.biens.categoryList.indexOf(category);
      if (index >= 0) {
        this.biens.categoryList.splice(index, 1);
      }
    }
  }

  // Agency search
  filterAgencies(): void {
    if (!this.agencySearch || this.agencySearch.trim() === '') {
      this.filteredAgencies = [...this.allAgencies];
    } else {
      const searchTerm = this.agencySearch.toLowerCase().trim();
      this.filteredAgencies = this.allAgencies.filter(agency => {
        if (!agency || !agency.name) return false;
        return agency.name.toLowerCase().includes(searchTerm);
      });
    }
  }

  clearAgencySearch(): void {
    this.agencySearch = '';
    this.filteredAgencies = [...this.allAgencies];
  }

  onAgencySelectOpened() {
    this.isAgencySelectOpen = true;
    this.agencySearch = '';
    this.filterAgencies();
  }

  onAgencySelectClosed() {
    this.isAgencySelectOpen = false;
  }

  // ========== MAIN IMAGE UPLOAD (SINGLE) ==========
  selectFileimg(event: any): void {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        icon: "error",
        title: "Fichier trop volumineux",
        text: "L'image dépasse la taille maximale de 5MB",
      });
      return;
    }
    
    this.selectedFile = file;
    this.uploadMainImage();
    event.target.value = '';
  }

  async uploadMainImage(): Promise<void> {
    if (!this.selectedFile) return;

    this.isUploading = true;
    this.uploadProgress = 0;

    Swal.fire({
      title: 'Téléchargement en cours...',
      html: 'Upload de l\'image principale',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });

    try {
      const folder = 'biens-immobiliers';
      const response = await this.awsService.uploadFile(this.selectedFile, folder).toPromise();
      
      Swal.close();
      
      if (response.success && response.urls && response.urls.length > 0) {
        const uploadedUrl = response.urls[0];
        this.mainImage = uploadedUrl;
        localStorage.setItem('bienImage', JSON.stringify(uploadedUrl));
        
        Swal.fire({
          icon: "success",
          title: "Image mise à jour",
          text: "L'image principale a été téléchargée avec succès",
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        throw new Error(response.error || 'Upload failed');
      }
    } catch (err: any) {
      console.error('Error uploading main image:', err);
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Erreur de téléchargement",
        text: "Une erreur est survenue lors du téléchargement de l'image",
      });
    } finally {
      this.isUploading = false;
      this.uploadProgress = 0;
      this.selectedFile = null;
    }
  }

  async removeMainImage(): Promise<void> {
    if (!this.mainImage) return;

    const result = await Swal.fire({
      title: 'Êtes-vous sûr?',
      text: "Voulez-vous vraiment supprimer cette image?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        await this.awsService.deleteFile(this.mainImage).toPromise();
        console.log('Image deleted from AWS:', this.mainImage);
      } catch (error) {
        console.warn('Could not delete image from AWS:', error);
      }
      
      this.mainImage = '';
      localStorage.removeItem('bienImage');
      
      Swal.fire({
        icon: "success",
        title: "Image supprimée",
        text: "L'image a été supprimée avec succès",
        timer: 2000,
        showConfirmButton: false
      });
    }
  }

  // Validation
  validateYouTubeUrl(url: string): boolean {
    if (!url) return true;
    const youtubePattern = /^(https?\:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
    return youtubePattern.test(url);
  }

  // Navigate to agency bien list
  private goToAgencyBiensList(): void {
    if (this.agencyId) {
      this.router.navigate(['/list-agence-biens', this.agencyId]);
    } else {
      // Fallback if no agency ID is available
      this._location.back();
    }
  }

  // Update property
  updateProperty(): void {
    // Check if upload is in progress
    if (this.isUploading) {
      Swal.fire({
        icon: 'warning',
        title: 'Téléchargement en cours',
        text: 'Veuillez attendre la fin du téléchargement de l\'image'
      });
      return;
    }

    // Validation
    if (!this.biens.name || !this.biens.description || !this.selectedAgencyName) {
      Swal.fire({
        icon: 'warning',
        title: 'Champs requis',
        text: 'Veuillez remplir tous les champs obligatoires (Nom, Description, Agence)'
      });
      return;
    }

    if (this.biens.videoUrlBien && !this.validateYouTubeUrl(this.biens.videoUrlBien)) {
      Swal.fire({
        icon: 'error',
        title: 'URL YouTube invalide',
        text: 'Veuillez entrer une URL YouTube valide'
      });
      return;
    }

    if (!this.mainImage) {
      Swal.fire({
        icon: 'warning',
        title: 'Image requise',
        text: 'Veuillez ajouter une image du bien'
      });
      return;
    }

    // Prepare update data (matching add component payload)
    const updateData = {
      "name": this.biens.name,
      "description": this.biens.description,
      "prix": this.biens.prix,
      "agencyName": this.selectedAgencyName,
      "categoryList": this.biens.categoryList || [],
      "imageUrl": this.mainImage,
      "videoUrlBien": this.biens.videoUrlBien,
      "nomCommercial": this.biens.nomCommercial || '',
      "numCommercial": this.biens.numCommercial || '',
      "emailCommercial": this.biens.emailCommercial || ''
      // type, role, promoTitle, promoImg, images array all removed
    };

    console.log('Updating property:', updateData);

    Swal.fire({
      title: 'Mise à jour en cours...',
      text: 'Veuillez patienter',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    this.bienImmobService.updateBiensImmobs(this.idBienImmobs, updateData).subscribe(
      (response: any) => {
        Swal.close();
        Swal.fire({
          icon: "success",
          title: "Succès!",
          text: "Le bien immobilier a été mis à jour avec succès",
          confirmButtonText: 'OK'
        }).then(() => {
          this.goToAgencyBiensList();
        });
      },
      (error) => {
        Swal.close();
        console.error('Error updating property:', error);
        
        let errorMessage = 'Échec de la mise à jour du bien immobilier';
        if (error.status === 400) {
          errorMessage = 'Données invalides. Veuillez vérifier les informations saisies.';
        } else if (error.status === 401) {
          errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        } else if (error.status === 500) {
          errorMessage = 'Erreur serveur. Veuillez réessayer plus tard.';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        Swal.fire({
          icon: "error",
          title: "Erreur",
          text: errorMessage,
        });
      }
    );
  }

  onCancel(): void {
    Swal.fire({
      title: 'Annuler les modifications?',
      text: 'Les modifications non enregistrées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, annuler',
      cancelButtonText: 'Continuer l\'édition'
    }).then((result) => {
      if (result.isConfirmed) {
        this.goToAgencyBiensList();
      }
    });
  }

  // Optional: Check AWS service health
  async checkAWSServiceHealth(): Promise<void> {
    try {
      const health = await this.awsService.checkHealth().toPromise();
      Swal.fire({
        icon: 'success',
        title: 'Service AWS Connecté',
        html: `
          <div style="text-align: left;">
            <p><strong>Statut:</strong> ${health.status}</p>
            <p><strong>Bucket:</strong> ${health.bucket}</p>
            <p><strong>Région:</strong> ${health.region}</p>
            <p><strong>Dernière vérification:</strong> ${health.timestamp}</p>
          </div>
        `,
        confirmButtonColor: '#3f51b5'
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Service AWS non disponible',
        text: 'Impossible de vérifier la connexion au service AWS'
      });
    }
  }

  getFileNameFromUrl(url: string): string {
    return this.awsService.extractFileName(url);
  }
}